/** School-level module toggles from auth user payload. */

import { isSubscriptionActive, isSubscriptionModuleEnabled } from './subscriptionStatus';

const DEFAULT_FEATURES = {
  is_fees_enabled: true,
  is_library_enabled: false,
  is_transport_enabled: false,
  is_branches_enabled: false,
  is_reception_enabled: false,
  is_payroll_enabled: false,
  is_exams_enabled: true,
  is_reports_enabled: true,
  is_sms_enabled: false,
  is_whatsapp_enabled: false,
  is_hostel_enabled: false,
  is_api_access_enabled: false,
  leave_require_class_teacher_approval: true,
  leave_require_admin_approval: true,
  leave_extra_approver_user_ids: [],
};

/** DB/API may send 0/1; `0 !== false` is true in JS — normalize explicitly. */
export function normalizeSchoolFeatureFlag(value, defaultWhenMissing = true) {
  if (value === undefined || value === null) {
    return defaultWhenMissing;
  }
  if (value === false || value === 0 || value === '0') {
    return false;
  }
  return Boolean(value);
}

export function getSchoolFeatures(userOrFeatures) {
  const raw = userOrFeatures?.school_features ?? userOrFeatures;
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_FEATURES };
  }
  const extraIds = Array.isArray(raw.leave_extra_approver_user_ids)
    ? raw.leave_extra_approver_user_ids.map((id) => parseInt(id, 10)).filter((id) => id > 0)
    : [];

  return {
    is_fees_enabled: normalizeSchoolFeatureFlag(raw.is_fees_enabled, true),
    is_library_enabled: normalizeSchoolFeatureFlag(raw.is_library_enabled, false),
    is_transport_enabled: normalizeSchoolFeatureFlag(raw.is_transport_enabled, false),
    is_branches_enabled: normalizeSchoolFeatureFlag(raw.is_branches_enabled, false),
    is_reception_enabled: normalizeSchoolFeatureFlag(raw.is_reception_enabled, false),
    is_payroll_enabled: normalizeSchoolFeatureFlag(raw.is_payroll_enabled, false),
    is_exams_enabled: normalizeSchoolFeatureFlag(raw.is_exams_enabled, true),
    is_reports_enabled: normalizeSchoolFeatureFlag(raw.is_reports_enabled, true),
    is_sms_enabled: normalizeSchoolFeatureFlag(raw.is_sms_enabled, false),
    is_whatsapp_enabled: normalizeSchoolFeatureFlag(raw.is_whatsapp_enabled, false),
    is_hostel_enabled: normalizeSchoolFeatureFlag(raw.is_hostel_enabled, false),
    is_api_access_enabled: normalizeSchoolFeatureFlag(raw.is_api_access_enabled, false),
    leave_require_class_teacher_approval: normalizeSchoolFeatureFlag(
      raw.leave_require_class_teacher_approval,
      true,
    ),
    leave_require_admin_approval: normalizeSchoolFeatureFlag(
      raw.leave_require_admin_approval,
      true,
    ),
    leave_extra_approver_user_ids: extraIds,
  };
}

/**
 * @param {object|null|undefined} user
 * @param {'fees'|'library'|'transport'|'whatsapp'|'whatsapp_announcements'|'reception'|'payroll'|'exams'|'reports'|'hostel'|'sms'|'api_access'} moduleKey
 */
export function isSchoolFeatureEnabled(user, moduleKey) {
  if (user?.role_slug === 'super_admin') {
    return true;
  }
  if (user?.subscription && !isSubscriptionActive(user)) {
    const core = ['fees', 'students', 'attendance', 'reports'];
    if (!core.includes(moduleKey)) {
      return false;
    }
  }
  if (user?.subscription?.features?.modules) {
    const subMap = {
      fees: 'fees',
      library: 'library',
      transport: 'transport',
      whatsapp: 'whatsapp',
      whatsapp_announcements: 'whatsapp',
      reception: 'reception',
      payroll: 'payroll',
      exams: 'exams',
      reports: 'reports',
      hostel: 'hostel',
      sms: 'sms',
      api_access: 'api_access',
    };
    const subKey = subMap[moduleKey];
    if (subKey && !isSubscriptionModuleEnabled(user, subKey)) {
      return false;
    }
  }
  if (moduleKey === 'fees') {
    return true;
  }
  const f = getSchoolFeatures(user);
  switch (moduleKey) {
    case 'library':
      return f.is_library_enabled;
    case 'transport':
      return f.is_transport_enabled;
    case 'branches':
      return f.is_branches_enabled;
    case 'reception':
      return f.is_reception_enabled;
    case 'payroll':
      return f.is_payroll_enabled;
    case 'exams':
      return f.is_exams_enabled;
    case 'reports':
      return f.is_reports_enabled;
    case 'sms':
      return f.is_sms_enabled;
    case 'hostel':
      return f.is_hostel_enabled;
    case 'api_access':
      return f.is_api_access_enabled;
    case 'whatsapp':
      return normalizeSchoolFeatureFlag(user?.whatsapp_config?.enabled, false) && f.is_whatsapp_enabled;
    case 'whatsapp_announcements':
      return normalizeSchoolFeatureFlag(
        user?.whatsapp_config?.announcements_enabled,
        true,
      );
    default:
      return true;
  }
}

/** Announcements under Communication (with WhatsApp queue) vs Academics (in-app only). */
export function isAnnouncementsViaWhatsapp(user) {
  return isSchoolFeatureEnabled(user, 'whatsapp')
    && isSchoolFeatureEnabled(user, 'whatsapp_announcements');
}

/**
 * @param {object} row fee type row from API
 */
export function classifyFeeType(row) {
  const category = String(row?.fee_category ?? '').toLowerCase().trim();
  if (['library', 'transport', 'standard'].includes(category)) {
    return category;
  }
  const code = String(row?.code ?? '').toUpperCase().trim();
  const name = String(row?.name ?? '').toLowerCase();
  if (code === 'LF' || name.includes('library')) return 'library';
  if (code === 'TRF' || name.includes('transport')) return 'transport';
  return 'standard';
}

/**
 * @param {object|null|undefined} user
 * @param {'standard'|'library'|'transport'} category
 */
export function isFeeCategoryVisible(user, category) {
  if (category === 'library') {
    return isSchoolFeatureEnabled(user, 'library');
  }
  if (category === 'transport') {
    return isSchoolFeatureEnabled(user, 'transport');
  }
  return true;
}

/**
 * @param {object|null|undefined} user
 * @param {object} feeTypeRow
 */
export function isFeeTypeVisible(user, feeTypeRow) {
  return isFeeCategoryVisible(user, classifyFeeType(feeTypeRow));
}

/** Map nav paths to optional school feature modules (library, transport, whatsapp announcements). */
export function schoolFeatureForPath(path) {
  if (!path) return null;
  if (path.startsWith('/whatsapp')) return 'whatsapp';
  if (path.startsWith('/reports/whatsapp-logs')) return 'whatsapp';
  if (path.startsWith('/library')) return 'library';
  if (path.startsWith('/transport')) return 'transport';
  if (path.startsWith('/reception')) return 'reception';
  if (path.startsWith('/reports')) return 'reports';
  if (path.startsWith('/exams')) return 'exams';
  return null;
}
